<script setup lang="ts">
import EmptyState from '@/components/EmptyState.vue'
import FinestButton from '@/components/FinestButton.vue'
import FinestCard from '@/components/FinestCard.vue'
import FinestInputText from '@/components/FinestInputText.vue'
import FinestSelect from '@/components/FinestSelect.vue'
import { estateObjectAddressLine } from '@/utils/estate-object'
import { estateObjectInvestment } from '@/utils/investment'
import { toNumber } from '@/utils/number'
import { projectStates, toProjectStatus, type ProjectStatus } from '@/utils/project-status'
import FinancingRequestWizardController from '@controllers/Investors/FinancingRequestWizardController'
import ProjectsController from '@controllers/Investors/ProjectsController'
import { Head, router } from '@inertiajs/vue3'
import { FilterMatchMode } from '@primevue/core/api'
import type { DataTableRowClickEvent, DataTableSortEvent } from 'primevue/datatable'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BusinessType = App.Enums.EstateObject.BusinessType
import ProjectData = App.Data.Project.ProjectData

const props = defineProps<{
  financingRequests: ProjectData[]
}>()

const { t, n } = useI18n()

const severities: Record<ProjectStatus, string> = {
  draft: 'secondary',
  'in-clearing': 'warn',
  live: 'success',
  completed: 'info',
  cancelled: 'danger',
}

type Grouping = 'none' | 'buyer' | 'owner'

interface FinancingRequestRow {
  id: string
  addressLine: string
  location: string
  businessType: BusinessType | null
  businessTypeLabel: string
  buyer: string
  owner: string
  investment: number | null
  equity: number | null
  status: ProjectStatus
  statusLabel: string
}

const businessTypeLabel = (businessType: BusinessType | null | undefined): string =>
  businessType ? t(`enums.businessType.${businessType}`) : t('empty')

/** A property that is not owned yet still belongs to a third party — the seller. */
const ownerLabel = (request: ProjectData): string =>
  request.estate_object?.is_purchase === false
    ? (request.estate_object?.owner?.name ?? t('withoutOwner'))
    : t('thirdPartyOwner')

const rows = computed<FinancingRequestRow[]>(() =>
  props.financingRequests.map((request) => {
    const estateObject = request.estate_object
    const status = toProjectStatus(request.status)

    return {
      id: request.id ?? '',
      addressLine: estateObject ? estateObjectAddressLine(estateObject) : (request.title ?? ''),
      location: estateObject ? `${estateObject.zip_code ?? ''} ${estateObject.city ?? ''}`.trim() : '',
      businessType: estateObject?.business_type ?? null,
      businessTypeLabel: businessTypeLabel(estateObject?.business_type),
      buyer: request.buyer?.name ?? t('withoutBuyer'),
      owner: ownerLabel(request),
      investment: estateObject ? estateObjectInvestment(estateObject) : null,
      equity: toNumber(request.equity_contribution),
      status,
      statusLabel: t(`enums.projectState.${status}`),
    }
  }),
)

const statusOptions = computed(() =>
  projectStates.map((state) => ({ value: state, label: t(`enums.projectState.${state}`) })),
)

const groupingOptions = computed<{ value: Grouping; label: string }[]>(() => [
  { value: 'none', label: t('filters.groupNone') },
  { value: 'buyer', label: t('filters.groupByBuyer') },
  { value: 'owner', label: t('filters.groupByOwner') },
])

const filters = ref({
  global: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  status: { value: null as ProjectStatus | null, matchMode: FilterMatchMode.EQUALS },
})

const grouping = ref<Grouping>('none')
const sortField = ref<string>('addressLine')
const sortOrder = ref<number>(1)

watch(grouping, (next) => {
  if (next !== 'none') {
    sortField.value = next
    sortOrder.value = 1
  }
})

const onSort = (event: DataTableSortEvent): void => {
  if (event.sortField !== grouping.value) {
    grouping.value = 'none'
  }
}

const filteredRows = ref<FinancingRequestRow[]>(rows.value)

watch(rows, (next) => (filteredRows.value = next))

const groupKey = (row: FinancingRequestRow): string => (grouping.value === 'owner' ? row.owner : row.buyer)

const groupedRows = computed(() => {
  const grouped = new Map<string, FinancingRequestRow[]>()

  if (grouping.value !== 'none') {
    filteredRows.value.forEach((row) => {
      const key = groupKey(row)

      grouped.set(key, [...(grouped.get(key) ?? []), row])
    })
  }

  return grouped
})

const formatEuro = (value: number | null): string => (value === null ? t('empty') : n(value, 'euros'))

const sumBy = (rows: FinancingRequestRow[], pick: (row: FinancingRequestRow) => number | null): number =>
  rows.reduce((total, row) => total + (pick(row) ?? 0), 0)

const groupSummary = (key: string): string => {
  const grouped = groupedRows.value.get(key) ?? []

  return t('groupSummary', {
    count: grouped.length,
    investment: formatEuro(sumBy(grouped, (row) => row.investment)),
    equity: formatEuro(sumBy(grouped, (row) => row.equity)),
  })
}

const openDetail = (event: DataTableRowClickEvent): void =>
  router.visit(ProjectsController.show((event.data as FinancingRequestRow).id).url)

const groupHeaderFullWidth = {
  rowGroupHeaderCell: ({ instance }: { instance: { columnsLength: number } }) => ({
    colspan: instance.columnsLength,
  }),
}
</script>

<template>
  <Head :title="t('heading')" />

  <div class="mb-6">
    <p class="section-label">{{ t('eyebrow') }}</p>
    <div class="flex items-center gap-2">
      <h1 class="page-title">{{ t('heading') }}</h1>
      <PageTitleHelp :text="t('subtitle')" />
    </div>
  </div>

  <FinestCard v-if="rows.length" :title="t('tableCard')" icon="pi pi-file-edit" flush>
    <template #actions>
      <Link :href="FinancingRequestWizardController.create()">
        <FinestButton variant="primary" size="md" :label="t('add')" icon="pi pi-plus" />
      </Link>
    </template>

    <div class="mb-4 flex flex-wrap items-center gap-3 px-5">
      <FinestInputText
        v-model="filters.global.value"
        icon="pi pi-search"
        :placeholder="t('filters.searchPlaceholder')"
        :aria-label="t('filters.search')"
        class="min-w-55 flex-1 sm:max-w-90" />

      <FinestSelect
        v-model="filters.status.value"
        :options="statusOptions"
        :placeholder="t('filters.allStates')"
        :aria-label="t('filters.state')"
        class="min-w-39.5" />

      <FinestSelect
        v-model="grouping"
        :options="groupingOptions"
        :aria-label="t('filters.grouping')"
        class="min-w-39.5" />

      <span class="text-ink-3 ml-auto whitespace-nowrap">
        {{ t('filters.resultCount', { shown: filteredRows.length, total: rows.length }) }}
      </span>
    </div>

    <div class="bg-surface overflow-hidden">
      <DataTable
        v-model:filters="filters"
        v-model:sort-field="sortField"
        v-model:sort-order="sortOrder"
        :value="rows"
        data-key="id"
        sort-mode="single"
        :global-filter-fields="['addressLine', 'location', 'businessTypeLabel', 'buyer', 'owner']"
        :row-group-mode="grouping === 'none' ? undefined : 'subheader'"
        :group-rows-by="grouping === 'none' ? undefined : grouping"
        row-hover
        class="finest-table"
        :pt="groupHeaderFullWidth"
        @sort="onSort"
        @value-change="filteredRows = $event as FinancingRequestRow[]"
        @row-click="openDetail">
        <template #empty>
          <p class="text-ink-3 py-8 text-center">{{ t('emptyResult') }}</p>
        </template>

        <template #groupheader="{ data }">
          <div class="flex flex-wrap items-baseline justify-between gap-2">
            <span>{{ groupKey(data as FinancingRequestRow) }}</span>
            <span class="text-ink-3 text-xs font-medium tracking-normal normal-case tabular-nums">
              {{ groupSummary(groupKey(data as FinancingRequestRow)) }}
            </span>
          </div>
        </template>

        <Column field="addressLine" :header="t('columns.estateObject')" sortable>
          <template #body="{ data }">
            <div class="max-w-65 min-w-0">
              <div class="truncate font-bold" :title="(data as FinancingRequestRow).addressLine">
                {{ (data as FinancingRequestRow).addressLine }}
              </div>
              <div class="text-ink-3 truncate text-xs">{{ (data as FinancingRequestRow).location || t('empty') }}</div>
            </div>
          </template>
          <template #footer>{{ t('total', { count: filteredRows.length }) }}</template>
        </Column>

        <Column field="businessType" sort-field="businessTypeLabel" :header="t('columns.businessType')" sortable>
          <template #body="{ data }">
            <div class="max-w-40 truncate" :title="(data as FinancingRequestRow).businessTypeLabel">
              {{ (data as FinancingRequestRow).businessTypeLabel }}
            </div>
          </template>
        </Column>

        <Column field="investment" :header="t('columns.investment')" sortable class="text-right tabular-nums">
          <template #body="{ data }">{{ formatEuro((data as FinancingRequestRow).investment) }}</template>
          <template #footer>{{ formatEuro(sumBy(filteredRows, (row) => row.investment)) }}</template>
        </Column>

        <Column field="equity" :header="t('columns.equity')" sortable class="text-right tabular-nums">
          <template #body="{ data }">{{ formatEuro((data as FinancingRequestRow).equity) }}</template>
          <template #footer>{{ formatEuro(sumBy(filteredRows, (row) => row.equity)) }}</template>
        </Column>

        <Column field="status" sort-field="statusLabel" :header="t('columns.status')" sortable>
          <template #body="{ data }">
            <Tag
              :value="(data as FinancingRequestRow).statusLabel"
              :severity="severities[(data as FinancingRequestRow).status]" />
          </template>
        </Column>
      </DataTable>
    </div>
  </FinestCard>

  <EmptyState v-else icon="pi pi-file-edit" :title="t('emptyState.title')" :description="t('emptyState.description')">
    <template #action>
      <Link :href="FinancingRequestWizardController.create()">
        <FinestButton variant="primary" size="md" :label="t('add')" icon="pi pi-plus" />
      </Link>
    </template>
  </EmptyState>
</template>

<style scoped src="../../../components/finest-table.css"></style>

<i18n>
{
  "de": {
    "empty": "—",
    "eyebrow": "Meine Finanzierung",
    "heading": "Finanzierungsanfragen",
    "subtitle": "Alle Anfragen auf einen Blick – vom Entwurf bis zum Abschluss. Suche und filtere nach Status und öffne eine Anfrage, um Objekte, Darlehen und Unterlagen zu bearbeiten.",
    "tableCard": "Deine Anfragen",
    "add": "Anfrage erstellen",
    "total": "Summe ({count})",
    "groupSummary": "{count} Anfragen · {investment} Investition · {equity} Eigenkapital",
    "withoutBuyer": "Ohne Käufer",
    "withoutOwner": "Ohne Besitzer",
    "thirdPartyOwner": "Fremder (Neukauf)",
    "emptyResult": "Keine Anfragen gefunden. Passe Suche oder Filter an.",
    "columns": {
      "estateObject": "Objekt",
      "businessType": "Geschäftsart",
      "investment": "Gesamtinvestition",
      "equity": "Eigenkapital",
      "status": "Status"
    },
    "filters": {
      "search": "Suche",
      "searchPlaceholder": "Adresse, Ort, Käufer oder Besitzer suchen …",
      "state": "Status",
      "allStates": "Alle Status",
      "grouping": "Gruppierung",
      "groupNone": "Gruppierung: keine",
      "groupByBuyer": "nach Käufer",
      "groupByOwner": "nach Besitzer",
      "resultCount": "{shown} von {total} Anfragen"
    },
    "emptyState": {
      "title": "Keine Finanzierungsanfragen",
      "description": "Du hast noch keine Anfrage angelegt. Wähle Objekte aus deinem Portfolio und stelle deine erste Finanzierungsanfrage zusammen."
    }
  },
  "en": {
    "empty": "—",
    "eyebrow": "My Financing",
    "heading": "Financing requests",
    "subtitle": "All your requests at a glance – from draft to completion. Search and filter by status and open a request to edit properties, loans and documents.",
    "tableCard": "Your requests",
    "add": "Create request",
    "total": "Total ({count})",
    "groupSummary": "{count} requests · {investment} investment · {equity} equity",
    "withoutBuyer": "Without buyer",
    "withoutOwner": "Without owner",
    "thirdPartyOwner": "Third party (purchase)",
    "emptyResult": "No requests found. Adjust your search or filters.",
    "columns": {
      "estateObject": "Property",
      "businessType": "Business type",
      "investment": "Total investment",
      "equity": "Equity",
      "status": "Status"
    },
    "filters": {
      "search": "Search",
      "searchPlaceholder": "Search address, city, buyer or owner …",
      "state": "Status",
      "allStates": "All states",
      "grouping": "Grouping",
      "groupNone": "Grouping: none",
      "groupByBuyer": "by buyer",
      "groupByOwner": "by owner",
      "resultCount": "{shown} of {total} requests"
    },
    "emptyState": {
      "title": "No financing requests",
      "description": "You have not created a request yet. Pick properties from your portfolio and put together your first financing request."
    }
  }
}
</i18n>
